import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowUpRight, Loader2, CheckCircle2 } from 'lucide-react';
import { useDisplayUnit } from '@/hooks/useDisplayUnit';
import { useWithdrawFlow, type WithdrawState } from '@/hooks/useWithdrawFlow';
import { AmountInput } from '@/components/shared/AmountInput';
import { formatXmr, parseUserInput } from '@/lib/monero/units';
import { selectProofsForAmount, calculateChange } from '@/lib/cashu/split';
import { useToast } from '@/hooks/useToast';
import type { WalletMintEntry } from '@/lib/storage/db';
import type { StoredProof } from '@/lib/storage/db';
import { sumProofs } from '@/lib/cashu/proof';

interface WithdrawDialogProps {
  mint: WalletMintEntry;
  proofs: StoredProof[];
  userPrivkey: string;
  fiatPrice?: number | null;
}

export function WithdrawDialog({ mint, proofs, userPrivkey, fiatPrice }: WithdrawDialogProps) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [destination, setDestination] = useState('');
  const { unit, toggle } = useDisplayUnit();
  const { state, startWithdraw, reset } = useWithdrawFlow();
  const { toast } = useToast();

  const balance = sumProofs(proofs);

  const handleStart = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({ title: 'Invalid amount', variant: 'destructive' });
      return;
    }
    if (!destination || destination.trim().length < 50) {
      toast({ title: 'Invalid Monero address', description: 'Enter a valid XMR address', variant: 'destructive' });
      return;
    }
    const piconero = parseUserInput(amount, unit);
    if (piconero <= 0) {
      toast({ title: 'Invalid amount', variant: 'destructive' });
      return;
    }

    const { selected } = selectProofsForAmount(proofs, piconero);
    if (selected.length === 0) {
      toast({ title: 'Insufficient balance', variant: 'destructive' });
      return;
    }

    await startWithdraw(selected, destination.trim(), mint.npub, userPrivkey);
  };

  const handleOpenChange = (o: boolean) => {
    setOpen(o);
    if (!o) reset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary">
          <ArrowUpRight className="h-4 w-4 mr-1" />
          Withdraw
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Withdraw from {mint.name}</DialogTitle>
          <DialogDescription>
            Redeem your ecash tokens for Monero (XMR). Balance: {formatXmr(balance, 6)} XMR
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {state.step === 'idle' || state.step === 'error' ? (
            <>
              <AmountInput
                  value={amount}
                  onChange={setAmount}
                  unit={unit}
                  onUnitToggle={toggle}
                  label="Amount to withdraw"
                  max={balance}
                  fiatPrice={fiatPrice}
                />
              <div className="space-y-2">
                <Label>Destination XMR Address</Label>
                <Input
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  placeholder="4..."
                  className="font-mono text-xs"
                />
              </div>
              <Button onClick={handleStart} className="w-full" disabled={!amount || !destination}>
                <ArrowUpRight className="h-4 w-4 mr-2" />
                Withdraw
              </Button>
              {state.step === 'error' && (
                <p className="text-xs text-destructive">{state.error}</p>
              )}
            </>
          ) : state.step === 'submitting' || state.step === 'awaiting_confirmation' ? (
            <div className="flex items-center justify-center py-8 flex-col gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                {state.step === 'submitting' ? 'Submitting withdrawal request...' : 'Waiting for mint confirmation...'}
              </p>
            </div>
          ) : state.step === 'done' ? (
            <div className="space-y-4 text-center py-4">
              <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
              <p className="font-semibold">{formatXmr(state.amount, 6)} XMR withdrawn!</p>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground">Transaction hash</p>
                <p className="text-xs font-mono break-all">{state.txHash}</p>
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
